import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { TooltipProvider } from "./components/ui/tooltip";
import { SessionExpiredModal } from "./components/auth/SessionExpiredModal";
import { initializeThemePreference } from "./lib/theme";
import {
  isBuiltDesktopApp as isBuiltDesktopAppFn,
  isDesktopRuntime,
  resolveWorkosRedirectUri,
  toSafeHttpUrl,
} from "./lib/logout";
import { startLongTaskObserver } from "./lib/perf/long-task-observer";
import { initPostHog } from "./lib/posthog";
import { initSentry } from "./lib/sentry";
import { hideBootSplash } from "./lib/boot-splash";
import { CallWindowPage } from "./components/calls/CallWindowPage";
import { evictCacheOnBuildShaChange } from "./lib/region-resolver";
import { normalizePostAuthReturnPath } from "./lib/post-auth";

declare const __APP_VERSION__: string;

// v0.1.42: self-heal stale region-resolver cache on app upgrade.
// Pre-WS-B6-DNS-activation builds populated localStorage with
// wrong-region kv/cf-ipcountry answers that mapped EU workspaces to
// "us"; the 7-day TTL kept them sticky across upgrades. Clear them
// once per version bump so the Worker's current kv answer is used on
// the next prefetch. Cheap, idempotent, safe in non-browser envs.
try {
  if (typeof window !== "undefined" && typeof __APP_VERSION__ === "string") {
    evictCacheOnBuildShaChange(__APP_VERSION__);
  }
} catch {
  // Defense-in-depth — `evictCacheOnBuildShaChange` already swallows
  // its own errors; this outer catch covers ReferenceError on
  // `__APP_VERSION__` in oddball environments.
}

// `dev:local --demoseed` clears the previously-selected channel on
// every launch so the app opens to InboxView instead of auto-restoring
// product-demo. Why: `useMarkChannelRead` (in MessageArea) auto-fires
// `actions.markChannelRead` whenever a channel is rendered with
// lastReadAt < latestMessageCreatedAt. If the renderer auto-restores
// the previously-selected channel on every boot, the seed's fresh
// `unread_count = 50` gets nuked to 0 before the operator can see the
// unread badge — the demo's headline state.
//
// Only fires when `VITE_DEMOSEED=true` is set by `scripts/dev-local.mjs
// --demoseed`. Other dev:local runs and packaged staging/prod builds
// never see this env var, so channel-restore behavior is unchanged.
try {
  if (
    typeof window !== "undefined" &&
    import.meta.env.VITE_DEMOSEED === "true"
  ) {
    localStorage.removeItem("ano-channel");
  }
} catch {
  // Don't block app boot on storage access errors.
}

const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID as string | undefined;
const configuredRedirectUri = import.meta.env.VITE_WORKOS_REDIRECT_URI as
  | string
  | undefined;

// The packaged desktop app uses a localhost:1420 callback server that catches
// the OAuth redirect and navigates the webview back.
const isDesktopBuiltApp = isBuiltDesktopAppFn();
const redirectUri = isDesktopBuiltApp
  ? "http://localhost:1420/callback"
  : resolveWorkosRedirectUri(window.location, configuredRedirectUri);

const PRODUCTION_API_URL = "https://api.ano.dev";

const rawApiUrl =
  (import.meta.env.VITE_API_URL as string | undefined) ?? PRODUCTION_API_URL;
const isDesktopRuntimeEnv = isDesktopRuntime(window.location);
document.documentElement.setAttribute(
  "data-runtime",
  isDesktopRuntimeEnv ? "desktop" : "web",
);
const isLocalhostApi =
  rawApiUrl.includes("localhost") || rawApiUrl.includes("127.0.0.1");

// Safety: in a built desktop app, a localhost API URL is unreachable since no
// local dev server runs. Always fall back to the production URL.
if (isDesktopBuiltApp && isLocalhostApi) {
  console.warn(
    `VITE_API_URL is set to a localhost URL (${rawApiUrl}) in a built desktop app. ` +
      `Falling back to ${PRODUCTION_API_URL}. Set VITE_API_URL to a reachable deployed URL to fix this.`,
  );
}
const apiUrl = new URL(
  isDesktopBuiltApp && isLocalhostApi ? PRODUCTION_API_URL : rawApiUrl,
);

initializeThemePreference();
startLongTaskObserver();
initPostHog();
initSentry();

// Detect liquid glass support (Electron on macOS 26+).
// Main process pushes state after did-finish-load; also query on startup
// in case the event already fired (e.g. renderer reload).
{
  const api = (window as unknown as Record<string, unknown>).__electronApi as
    | {
        hasLiquidGlass: () => Promise<boolean>;
        onLiquidGlassState: (cb: (active: boolean) => void) => () => void;
      }
    | undefined;
  if (api?.onLiquidGlassState) {
    const setGlass = (active: boolean) => {
      console.log("[liquid-glass] renderer setGlass:", active);
      if (active) {
        document.documentElement.setAttribute("data-liquid-glass", "");
      } else {
        document.documentElement.removeAttribute("data-liquid-glass");
      }
    };
    // Listen for push from main process
    api.onLiquidGlassState(setGlass);
    // Also poll in case did-finish-load already fired (renderer reload)
    api
      .hasLiquidGlass()
      .then(setGlass)
      .catch(() => {});
  }
}

const root = document.getElementById("root") as HTMLElement;
const hasSafeConfiguredRedirectUri =
  toSafeHttpUrl(configuredRedirectUri) !== null;
const normalizePostAuthPath = (params?: {
  state?: Record<string, string> | null;
}) => {
  const returnTo =
    params?.state?.returnTo ?? sessionStorage.getItem("ano:auth-return-url");
  sessionStorage.removeItem("ano:auth-return-url");

  if (typeof returnTo === "string") {
    try {
      const url = new URL(returnTo);
      // Persist any invite token to localStorage so it survives OAuth redirects
      const inviteToken =
        url.searchParams.get("invite") ?? url.searchParams.get("space_invite");
      if (inviteToken) {
        try {
          localStorage.setItem(
            "ano-pending-invite-token",
            JSON.stringify({ token: inviteToken, ts: Date.now() }),
          );
        } catch {
          // ignore — localStorage may be unavailable
        }
      }
    } catch {
      // Invalid URL — fall through to default
    }

    const normalized = normalizePostAuthReturnPath(
      returnTo,
      window.location.origin,
    );
    if (normalized) {
      window.history.replaceState({}, "", normalized);
      return;
    }
  }

  // Default: clear /callback path
  if (window.location.pathname === "/callback") {
    window.history.replaceState({}, "", "/");
  }
};

if (isDesktopRuntimeEnv && !hasSafeConfiguredRedirectUri) {
  console.warn(
    "Missing a valid http(s) VITE_WORKOS_REDIRECT_URI for desktop app; auth redirects may fail.",
  );
}

// Static integration-callback landing pages — render before the full app
// initializes so we don't require AuthKit / Zero / etc. just to say
// "you're connected, close this tab."
const integrationsDonePath = "/integrations/pipedream/done";
const isPipedreamDonePath =
  window.location.pathname === integrationsDonePath ||
  window.location.pathname === `${integrationsDonePath}/`;

const isCallWindowPath =
  window.location.pathname === "/call-window" ||
  window.location.pathname === "/call-window/";

if (isCallWindowPath) {
  hideBootSplash();
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <CallWindowPage />
    </React.StrictMode>,
  );
} else if (isPipedreamDonePath) {
  // Lazy import keeps the landing page off the main app's bundle dependency.
  import("./components/integrations/PipedreamDonePage.js")
    .then(({ PipedreamDonePage }) => {
      ReactDOM.createRoot(root).render(
        <React.StrictMode>
          <PipedreamDonePage />
        </React.StrictMode>,
      );
    })
    .catch((err) => {
      // If the chunk fails to load (network blip, deploy churn), fall back to
      // a minimal inline message so the user isn't staring at a blank tab.
      console.error("[PipedreamDonePage] lazy import failed", err);
      hideBootSplash();
      ReactDOM.createRoot(root).render(
        <React.StrictMode>
          <div className="flex h-screen items-center justify-center bg-background text-foreground">
            <p className="text-sm text-muted-foreground">
              Connection completed. You can close this tab.
            </p>
          </div>
        </React.StrictMode>,
      );
    });
} else if (!clientId) {
  hideBootSplash();
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <div className="flex h-screen items-center justify-center bg-background text-foreground">
        <div className="w-[28rem] space-y-3 rounded-lg border border-border p-6 text-center">
          <h1 className="text-xl font-semibold">
            Missing WorkOS configuration
          </h1>
          <p className="text-sm text-muted-foreground">
            `VITE_WORKOS_CLIENT_ID` is not set for this build.
          </p>
          <p className="text-xs text-muted-foreground">
            Rebuild with your staging client ID in the build environment.
          </p>
        </div>
      </div>
    </React.StrictMode>,
  );
} else {
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <AuthKitProvider
        clientId={clientId}
        redirectUri={redirectUri}
        onRedirectCallback={normalizePostAuthPath}
        apiHostname={apiUrl.hostname}
        https={apiUrl.protocol === "https:"}
        port={Number(apiUrl.port) || undefined}
        devMode={
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1" ||
          isDesktopBuiltApp
        }
      >
        <TooltipProvider delayDuration={300}>
          <App />
          <SessionExpiredModal />
        </TooltipProvider>
      </AuthKitProvider>
    </React.StrictMode>,
  );
}
